import { apiEndpoint } from '@/lib/consts';
import fetchWithAuth from '@/lib/apis';
import { ORGANIZATION_HEADER } from '@/lib/tab-organization';

type FetchInit = Parameters<typeof fetch>[1];

// Strategy names the organisation on every call explicitly (#4999) rather than relying on the tab's organisation that
// fetchWithAuth sends (#6773): a caller-named organisation is the one sent.

// The org id is threaded explicitly from the calling component/query options
// (#4999) — no module-level state — and the request goes through
// ``fetchWithAuth`` so strategy calls get the same 401 → sign-in redirect and
// timeout handling as the rest of the app.
async function request<T>(path: string, orgId: number, init?: FetchInit): Promise<T> {
  const headers: Record<string, string> = {
    [ORGANIZATION_HEADER]: String(orgId),
  };
  if (init?.headers) Object.assign(headers, init.headers as Record<string, string>);
  const response = await fetchWithAuth(`${apiEndpoint}${path}`, { ...init, headers });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Strategy request failed: ${response.status}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

/** Which KPI the user has chosen as their goal. AOV is never a focus — it's
 * a business input, not a Google Ads metric. */
export type FocusKpi = 'budget' | 'cpa' | 'conversions' | 'roas';

export interface AccountKpiTargetsWire {
  organisation_id: number;
  googleadsaccount_id: number;
  cpa: number;
  roas: number;
  spend: number;
  conversions: number;
  aov: number;
  focus_kpi: FocusKpi | null;
  /** The user's explicit goal for ``focus_kpi`` — authoritative source of
   * intent. Denormalised copy of the corresponding per-KPI column, written
   * at save time so downstream consumers don't have to switch on
   * ``focus_kpi`` to find the user's goal. */
  goal_value: number | null;
  updated_at: string;
}

export interface AccountKpiTargetsUpsert {
  googleadsaccount_id: number;
  cpa: number;
  roas: number;
  spend: number;
  conversions: number;
  aov: number;
  focus_kpi: FocusKpi | null;
  goal_value: number | null;
}

export function getAccountKpiTargets(
  orgId: number,
  googleadsaccount_id: number,
): Promise<AccountKpiTargetsWire> {
  return request<AccountKpiTargetsWire>(
    `/api/strategy/kpi-targets?googleadsaccount_id=${googleadsaccount_id}`,
    orgId,
  );
}

export function putAccountKpiTargets(
  orgId: number,
  body: AccountKpiTargetsUpsert,
): Promise<AccountKpiTargetsWire> {
  return request<AccountKpiTargetsWire>('/api/strategy/kpi-targets', orgId, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/** Opaque Goals-page state blob. Shape is owned by the frontend
 * (`strategy/index.tsx`); the server round-trips it verbatim. */
export interface StrategyGoalsStateBlob {
  enabledChannels?: Record<string, boolean>;
  completedGoals?: Record<string, boolean>;
}

export interface StrategyGoalsStateWire {
  organisation_id: number;
  state: StrategyGoalsStateBlob;
  updated_at: string;
}

export interface StrategyGoalsStateUpsert {
  state: StrategyGoalsStateBlob;
}

// Org is resolved server-side from the active-org header that `request`
// already sends; no account/org id in the path or body.
export function getStrategyGoalsState(orgId: number): Promise<StrategyGoalsStateWire> {
  return request<StrategyGoalsStateWire>('/api/strategy/goals-state', orgId);
}

export function putStrategyGoalsState(
  orgId: number,
  body: StrategyGoalsStateUpsert,
): Promise<StrategyGoalsStateWire> {
  return request<StrategyGoalsStateWire>('/api/strategy/goals-state', orgId, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

// -------------------------------------------------------------------------
// Strategy-doc generator (call transcripts → strategy doc, scoped per
// org + business). The backend writes the markdown + audit sidecar to GCS
// and persists a row per run so the agent is fully auditable. Only the
// Context-tab read/approve/edit/upload surface is wired on this release line;
// the generation endpoints land with their UI later.
// -------------------------------------------------------------------------

export type StrategyDocStatus = 'queued' | 'running' | 'succeeded' | 'failed';

export type StrategyDocSource = 'generated' | 'uploaded';

export interface StrategyDocRunSummary {
  id: number;
  business_id: number;
  /** Display name (#5953): doc H1 / filename, renamable; null on legacy rows. */
  title: string | null;
  status: StrategyDocStatus;
  /** 'generated' = call-transcript pipeline; 'uploaded' = user-supplied doc. */
  source: StrategyDocSource;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  /** ISO timestamp of human sign-off, or null when not approved. */
  approved_at: string | null;
  /** When the working copy last changed (editor/regen/restore saves); null =
   * untouched since generation. Newer than the derived sheet = stale plan. */
  markdown_updated_at: string | null;
  error_text: string | null;
  transcript_count: number;
  output_markdown_gcs_uri: string | null;
  output_audit_gcs_uri: string | null;
  skills_used: string[];
  /** Immutable snapshot count (#6201) — shown as a chip on the doc row. */
  doc_version_count: number;
}

export interface StrategyDocRunList {
  organisation_id: number;
  business_id: number;
  runs: StrategyDocRunSummary[];
}

export function listStrategyDocRuns(
  orgId: number,
  businessId: number,
): Promise<StrategyDocRunList> {
  return request<StrategyDocRunList>(`/api/strategy/doc/runs?business_id=${businessId}`, orgId);
}

/** Queue a strategy-doc generation run for a business. The backend gathers
 * every transcript in the business's strategy store (reviewed calls are
 * bridged in automatically) plus the agreed-call brief; generation runs
 * async on Celery and the returned run starts `queued`. */
export function createStrategyDocRun(
  orgId: number,
  businessId: number,
): Promise<StrategyDocRunSummary> {
  return request<StrategyDocRunSummary>('/api/strategy/doc/runs', orgId, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ business_id: businessId }),
  });
}

export async function getStrategyDocMarkdown(orgId: number, runId: number): Promise<string> {
  // Plain-text response (text/markdown), not JSON, so the shared `request`
  // helper (which calls `.json()`) doesn't fit. Still goes through
  // `fetchWithAuth` for the same auth/timeout handling, with the org header.
  const response = await fetchWithAuth(`${apiEndpoint}/api/strategy/doc/runs/${runId}/markdown`, {
    headers: { Accept: 'text/markdown', [ORGANIZATION_HEADER]: String(orgId) },
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Markdown fetch failed: ${response.status}`);
  }
  return response.text();
}

/** Rename a document (#5953). Display metadata only — content untouched. */
export function renameStrategyDocRun(orgId: number, runId: number, title: string): Promise<StrategyDocRunSummary> {
  return request<StrategyDocRunSummary>(`/api/strategy/doc/runs/${runId}/title`, orgId, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
  });
}

/** Soft-delete (or restore) a run: hidden from the list, audit trail intact.
 * 409 while the document is approved — revoke approval first. */
export function setStrategyDocArchived(
  orgId: number,
  runId: number,
  archived: boolean,
): Promise<StrategyDocRunSummary> {
  return request<StrategyDocRunSummary>(`/api/strategy/doc/runs/${runId}/archive`, orgId, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ archived }),
  });
}

// ---------------------------------------------------------- document versions (#6201)
export interface StrategyDocVersion {
  version: number;
  label: string | null;
  created_by: number | null;
  created_at: string | null;
}

export function listStrategyDocVersions(orgId: number, runId: number): Promise<StrategyDocVersion[]> {
  return request<StrategyDocVersion[]>(`/api/strategy/doc/runs/${runId}/versions`, orgId);
}

/** Snapshot the current working document as the next immutable version. */
export function saveStrategyDocVersion(
  orgId: number,
  runId: number,
  label?: string,
): Promise<StrategyDocVersion[]> {
  return request<StrategyDocVersion[]>(`/api/strategy/doc/runs/${runId}/versions`, orgId, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ label: label || null }),
  });
}

export async function getStrategyDocVersionMarkdown(
  orgId: number,
  runId: number,
  version: number,
): Promise<string> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/api/strategy/doc/runs/${runId}/versions/${version}/markdown`,
    { headers: { Accept: 'text/markdown', [ORGANIZATION_HEADER]: String(orgId) } },
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Version fetch failed: ${response.status}`);
  }
  return response.text();
}

/** Copy a snapshot back into the working copy; returns the updated working markdown. */
export async function restoreStrategyDocVersion(
  orgId: number,
  runId: number,
  version: number,
): Promise<string> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/api/strategy/doc/runs/${runId}/versions/${version}/restore`,
    { method: 'POST', headers: { Accept: 'text/markdown', [ORGANIZATION_HEADER]: String(orgId) } },
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Version restore failed: ${response.status}`);
  }
  return response.text();
}

/** Rewrite ONE section of a run's document to the user's feedback (one LLM
 * call, a few seconds). Manual edits in other sections survive: the rewrite is
 * spliced into the current persisted copy. Returns the full updated markdown. */
export async function regenerateStrategyDocSection(
  orgId: number,
  runId: number,
  sectionHeading: string,
  feedback: string,
): Promise<string> {
  const response = await fetchWithAuth(`${apiEndpoint}/api/strategy/doc/runs/${runId}/regenerate-section`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/markdown',
      [ORGANIZATION_HEADER]: String(orgId),
    },
    body: JSON.stringify({ section_heading: sectionHeading, feedback }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Section regeneration failed: ${response.status}`);
  }
  return response.text();
}

export async function saveStrategyDocMarkdown(
  orgId: number,
  runId: number,
  markdown: string,
): Promise<string> {
  // Mirrors getStrategyDocMarkdown: the endpoint returns the saved markdown as
  // plain text (not JSON). PUT the full edited document; the backend overwrites
  // the run's GCS blob.
  const response = await fetchWithAuth(`${apiEndpoint}/api/strategy/doc/runs/${runId}/markdown`, {
    method: 'PUT',
    headers: { Accept: 'text/markdown', [ORGANIZATION_HEADER]: String(orgId) },
    body: JSON.stringify({ markdown }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Markdown save failed: ${response.status}`);
  }
  return response.text();
}

/** Approve (or revoke approval of) a strategy document. One endpoint toggles
 * both directions via the `approved` flag. */
export function setStrategyDocApproval(
  orgId: number,
  runId: number,
  approved: boolean,
): Promise<StrategyDocRunSummary> {
  return request<StrategyDocRunSummary>(`/api/strategy/doc/runs/${runId}/approval`, orgId, {
    method: 'POST',
    body: JSON.stringify({ approved }),
  });
}

/** Upload a finished strategy document directly (no transcripts, no agent).
 * It is stored as a succeeded run with source='uploaded' and appears alongside
 * generated docs. Multipart body — `fetchWithAuth` omits the JSON Content-Type
 * for FormData so the browser sets the multipart boundary. */
export interface DocumentUploadResponse {
  /** What the upload became after classification (#6230). */
  kind: 'call' | 'document';
  document: StrategyDocRunSummary | null;
  call_transcript_id: number | null;
}

export interface DocumentListItem {
  kind: 'call' | 'document';
  id: number;
  title: string | null;
  source: string;
  filename: string | null;
  created_at: string;
  occurred_at: string | null;
  action_count: number;
  extraction_status: string | null;
  attendees: { name: string; picture: string | null }[];
  added_by: string | null;
}

/** The unified context library: calls + general documents, newest first. */
export async function listDocuments(orgId: number, businessId: number): Promise<DocumentListItem[]> {
  const response = await fetchWithAuth(`${apiEndpoint}/api/strategy/doc/documents?business_id=${businessId}`, {
    headers: { [ORGANIZATION_HEADER]: String(orgId) },
  });
  if (!response.ok) throw new Error('The document library could not be loaded');
  return (await response.json()).items as DocumentListItem[];
}

export async function uploadStrategyDoc(
  orgId: number,
  businessId: number,
  file: File,
): Promise<DocumentUploadResponse> {
  const form = new FormData();
  form.append('file', file);
  const response = await fetchWithAuth(
    `${apiEndpoint}/api/strategy/doc/uploads?business_id=${businessId}`,
    {
      method: 'POST',
      body: form,
      headers: { [ORGANIZATION_HEADER]: String(orgId) },
    },
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Document upload failed: ${response.status}`);
  }
  return response.json() as Promise<DocumentUploadResponse>;
}
